import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxProgressBarComponent } from '@flowx/angular-ui-toolkit'
import {
  ProgressBarTemplateConfig,
  accessibilityLabelOf,
  localizeTemplateText,
} from '@flowx/core-sdk'

import { getBoolExpressionSignal, getComputedValueSignal, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-progress-bar-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [FlxProgressBarComponent, FlxThemeModule, FlxStyleModule],
  templateUrl: './progress-bar-wrapper.component.html',
  styleUrl: './progress-bar-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxProgressBarWrapperComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<ProgressBarTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)

  // expressions
  isHidden: Signal<boolean>
  progressValue: Signal<string>
  ariaLabel = computed(() =>
    localizeTemplateText(accessibilityLabelOf(this.flowxProps().accessibility))
  )

  progress = computed(() => {
    const parsed = parseFloat(this.progressValue?.() || '0')
    return isNaN(parsed) ? 0 : parsed
  })

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const { hide, progress, isComputedProgress } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.progressValue = isComputedProgress
        ? getComputedValueSignal(progress || '0', this.templateConfig())
        : getDynamicPropSignal(progress || '0', this.templateConfig())
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
